import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus,
  Edit,
  Trash2,
  Heart,
  Shield,
  Eye,
  Stethoscope,
  Pill,
  Baby,
  Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

const iconOptions = [
  { value: 'Heart', icon: Heart, label: 'Heart' },
  { value: 'Shield', icon: Shield, label: 'Shield' },
  { value: 'Eye', icon: Eye, label: 'Eye (Vision)' },
  { value: 'Stethoscope', icon: Stethoscope, label: 'Stethoscope' },
  { value: 'Pill', icon: Pill, label: 'Pill (Pharmacy)' },
  { value: 'Baby', icon: Baby, label: 'Baby (Family)' },
  { value: 'Activity', icon: Activity, label: 'Activity (Wellness)' },
];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Heart',
    parent_category_id: '',
    sort_order: 0,
    is_active: true
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const user = await api.auth.me();
      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId);
        } else {
          setCategories([]);
          setProducts([]);
        }
      } else {
        await loadData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId) => {
    try {
      const usingEmployerId = employerId || undefined;
      const useTenantFilter = Boolean(usingEmployerId);

      const [categoriesData, productsData] = await Promise.all(
        useTenantFilter
          ? [
              api.entities.BenefitCategory.filter({ employer_id: usingEmployerId }),
              api.entities.BenefitProduct.filter({ employer_id: usingEmployerId })
            ]
          : [
              api.entities.BenefitCategory.list(),
              api.entities.BenefitProduct.list()
            ]
      );
      setCategories(categoriesData);
      setProducts(productsData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value);
    } finally {
      setLoading(false);
    }
  };

  const getProductCount = (categoryId) => {
    return products.filter(p => p.category_id === categoryId).length;
  };

  const getIcon = (iconName) => {
    const iconOption = iconOptions.find(i => i.value === iconName);
    return iconOption?.icon || Heart;
  };

  const parentCategories = categories.filter(c => !c.parent_category_id);

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        icon: category.icon || 'Heart',
        parent_category_id: category.parent_category_id || '',
        sort_order: category.display_order || 0,
        is_active: category.is_active !== false
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        icon: 'Heart',
        parent_category_id: '',
        sort_order: 0,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        display_order: formData.sort_order || 0,
        parent_category_id: formData.parent_category_id || null,
        ...(isSystemAdmin && selectedEmployerId ? { employer_id: selectedEmployerId } : {})
      };

      // Don't send the UI-only alias
      delete data.sort_order;

      if (editingCategory) {
        await api.entities.BenefitCategory.update(editingCategory.id, data);
        toast.success('Category updated');
      } else {
        await api.entities.BenefitCategory.create(data);
        toast.success('Category created');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save category');
    }
  };

  const handleDelete = async (category) => {
    const productCount = getProductCount(category.id);
    if (productCount > 0) {
      toast.error(`Cannot delete: ${productCount} products are using this category`);
      return;
    }

    try {
      await api.entities.BenefitCategory.delete(category.id);
      toast.success('Category deleted');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete category');
    }
  };

  const confirmDelete = (category) => {
    const productCount = getProductCount(category.id);
    const impactText = productCount > 0
      ? 'Active products reference this category.'
      : 'No products reference this category.';

    openConfirm({
      title: 'Delete this category?',
      description: `${category.name} will be removed. ${impactText} Deleting does not remove products, but you may need to reassign them.`,
      confirmLabel: 'Delete category',
      isDestructive: true,
      onConfirm: () => handleDelete(category)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Benefit Categories</h1>
          <p className="text-slate-500 mt-1">
            Organize benefits into categories and subcategories
          </p>
        </div>
        <Button 
          onClick={() => openModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((employer) => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.filter(c => !c.parent_category_id).map((category, idx) => {
          const Icon = getIcon(category.icon);
          const subcategories = categories.filter(c => c.parent_category_id === category.id);
          
          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className={`h-full ${category.is_active === false ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        {getProductCount(category.id)} products
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openModal(category)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600"
                        onClick={() => confirmDelete(category)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {category.description && (
                    <p className="text-sm text-slate-600 mb-4">{category.description}</p>
                  )}
                  
                  {/* Subcategories */}
                  {subcategories.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Subcategories</p>
                      {subcategories.map(sub => {
                        const SubIcon = getIcon(sub.icon);
                        return (
                          <div 
                            key={sub.id}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <SubIcon className="w-4 h-4 text-slate-400" />
                              <span className="text-sm">{sub.name}</span>
                              <span className="text-xs text-slate-400">
                                ({getProductCount(sub.id)})
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openModal(sub)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select 
                value={formData.icon}
                onValueChange={(v) => setFormData({ ...formData, icon: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map(option => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {option.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Parent Category (optional)</Label>
              <Select 
                value={formData.parent_category_id}
                onValueChange={(v) => setFormData({ ...formData, parent_category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None (top-level category)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>None (top-level)</SelectItem>
                  {parentCategories
                    .filter(c => c.id !== editingCategory?.id)
                    .map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: Number.parseInt(e.target.value) || 0 })}
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
              {editingCategory ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}